import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

export class ExportUtils {
    /**
     * Export data to Excel
     */
    static exportToExcel(data, filename = 'expense_report') {
        console.log('ExportToExcel called with:', data.length, 'records');

        try {
            if (!data || data.length === 0) {
                console.error('No data to export');
                throw new Error('No data to export');
            }

            // Prepare data for Excel
            const excelData = data.map(item => ({
                'Date': format(new Date(item.date), 'dd/MM/yyyy'),
                'Title': item.title || '',
                'Description': item.description || '',
                'Category': item.category || 'Uncategorized',
                'Payment Method': item.payment_method || 'M-Pesa',
                'Amount': parseFloat(item.amount).toFixed(2),
                'Status': item.is_recurring ? 'Recurring' : 'One-time',
            }));

            console.log('Prepared Excel data:', excelData.length, 'rows');

            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            ws['!cols'] = [
                { wch: 15 }, // Date
                { wch: 30 }, // Title
                { wch: 40 }, // Description
                { wch: 20 }, // Category
                { wch: 20 }, // Payment Method
                { wch: 15 }, // Amount
                { wch: 15 }, // Status
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Expenses');

            // Generate Excel file
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/octet-stream' });

            console.log('Saving Excel file:', `${filename}.xlsx`);
            saveAs(blob, `${filename}.xlsx`);

            return true;
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            throw new Error(`Excel export failed: ${error.message}`);
        }
    }

    /**
     * Export data to PDF
     */
    static exportToPDF(data, title = 'Expense Report') {
        console.log('ExportToPDF called with:', data.length, 'records');

        try {
            if (!data || data.length === 0) {
                console.error('No data to export');
                throw new Error('No data to export');
            }

            const doc = new jsPDF('landscape', 'mm', 'a4');

            // Add header
            doc.setFontSize(18);
            doc.setTextColor(33, 33, 33);
            doc.text(title, 14, 20);

            // Add date
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 28);

            // Add summary
            const totalAmount = data.reduce((sum, item) => sum + parseFloat(item.amount), 0);
            const totalTransactions = data.length;
            const categories = new Set(data.map(item => item.category));

            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            const summaryText = [
                `Total Expenses: KES ${totalAmount.toFixed(2)}`,
                `Total Transactions: ${totalTransactions}`,
                `Categories: ${categories.size}`,
                `Average Amount: KES ${totalTransactions > 0 ? (totalAmount / totalTransactions).toFixed(2) : 0}`
            ];
            doc.text(summaryText, 14, 36);

            // Add table
            const tableData = data.map(item => [
                format(new Date(item.date), 'dd/MM/yyyy'),
                item.title || '',
                (item.description || '').substring(0, 30),
                item.category || 'Uncategorized',
                item.payment_method || 'M-Pesa',
                `KES ${parseFloat(item.amount).toFixed(2)}`,
                item.is_recurring ? 'Yes' : 'No'
            ]);

            console.log('PDF table data prepared:', tableData.length, 'rows');

            autoTable(doc, {
                head: [['Date', 'Title', 'Description', 'Category', 'Payment Method', 'Amount', 'Recurring']],
                body: tableData,
                startY: 44,
                theme: 'grid',
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                },
                headStyles: {
                    fillColor: [33, 150, 243],
                    textColor: [255, 255, 255],
                    fontSize: 9,
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [240, 245, 255],
                },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 35 },
                    2: { cellWidth: 40 },
                    3: { cellWidth: 30 },
                    4: { cellWidth: 30 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 20 },
                },
            });

            // Save PDF
            const pdfFilename = `${title.replace(/\s+/g, '_')}.pdf`;
            console.log('Saving PDF file:', pdfFilename);
            doc.save(pdfFilename);

            return true;
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            throw new Error(`PDF export failed: ${error.message}`);
        }
    }

    /**
     * Export data to CSV
     */
    static exportToCSV(data, filename = 'expense_report') {
        console.log('ExportToCSV called with:', data.length, 'records');

        try {
            if (!data || data.length === 0) {
                console.error('No data to export');
                throw new Error('No data to export');
            }

            // Prepare headers
            const headers = ['Date', 'Title', 'Description', 'Category', 'Payment Method', 'Amount', 'Status'];

            // Prepare rows
            const rows = data.map(item => [
                format(new Date(item.date), 'dd/MM/yyyy'),
                `"${(item.title || '').replace(/"/g, '""')}"`,
                `"${(item.description || '').replace(/"/g, '""')}"`,
                `"${(item.category || 'Uncategorized').replace(/"/g, '""')}"`,
                `"${(item.payment_method || 'M-Pesa').replace(/"/g, '""')}"`,
                parseFloat(item.amount).toFixed(2),
                item.is_recurring ? 'Recurring' : 'One-time'
            ]);

            // Build CSV content
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            console.log('Saving CSV file:', `${filename}.csv`);
            saveAs(blob, `${filename}.csv`);

            return true;
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            throw new Error(`CSV export failed: ${error.message}`);
        }
    }
}

export default ExportUtils;